import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { EGoogleAdsKeywordMatchType } from '@feathr/blackbox';
import { Button, Label, Well } from '@feathr/components';

import { createKeyword } from '../AdStep.factories';
import type { IAdGroupKeyword } from '../AdStep.types';
import KeywordRow from './KeywordRow';

import * as styles from './AdGroupEditor.css';

interface INegativeKeywordFieldsProps {
  negativeKeywords: IAdGroupKeyword[];
  /** Messages for every keyword that has any, keyed by keyword key. Shared with the
   * positive editor, because the two are one list on the model. */
  keywordErrors: Record<string, string[]>;
  onChange: (negativeKeywords: IAdGroupKeyword[]) => void;
  disabled?: boolean;
}

/**
 * Terms that suppress the ad. Same row-per-keyword editing as the positive keywords, minus
 * the match type — negative keywords don't carry one, and the single-word policy check
 * doesn't apply to them.
 *
 * @param props - {@link INegativeKeywordFieldsProps}.
 */
function NegativeKeywordFields({
  negativeKeywords,
  keywordErrors,
  onChange,
  disabled = false,
}: Readonly<INegativeKeywordFieldsProps>): JSX.Element {
  const { t } = useTranslation();

  function handleAdd(): void {
    onChange([...negativeKeywords, createKeyword('', EGoogleAdsKeywordMatchType.BROAD)]);
  }

  function handleRemove(index: number): void {
    onChange(negativeKeywords.filter((_, i) => i !== index));
  }

  function handleTextChange(index: number, text: string): void {
    onChange(negativeKeywords.map((keyword, i) => (i === index ? { ...keyword, text } : keyword)));
  }

  return (
    <>
      {/* On the heading, the way the sitelink sections carry what they have to explain. */}
      <Label
        tooltip={t("Searches containing these terms won't trigger your ads.")}
        weight={'medium'}
      >
        {t('Negative keywords')}
      </Label>
      <Well layout={'vertical'} theme={'white'} width={'full'}>
        {!!negativeKeywords.length && (
          <div className={styles.negativeKeywordRows}>
            <Label className={styles.columnHeader}>{t('Negative keyword')}</Label>
            {/* Nothing to name above the remove buttons. */}
            <div />
            {negativeKeywords.map((keyword, index) => (
              <KeywordRow
                disabled={disabled}
                errors={keywordErrors[keyword.key]}
                key={keyword.key}
                keyword={keyword}
                number={index + 1}
                onRemove={handleRemove}
                onTextChange={handleTextChange}
              />
            ))}
          </div>
        )}
        {/* A Well's vertical layout stretches its children, so the action needs a row to sit in. */}
        <Flex className={styles.actions} wrap={'wrap'}>
          <Button
            disabled={disabled}
            name={'add-negative-keyword'}
            onClick={handleAdd}
            prefix={<FontAwesomeIcon icon={faPlus} />}
          >
            {t('Add negative keyword')}
          </Button>
        </Flex>
      </Well>
    </>
  );
}

export default NegativeKeywordFields;
